import { Fragment } from 'react';
import type { RiskMetric } from '@amzn/gbsa-helios-typescript-client';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';

import { useMessages } from 'src/v2/shared/i18n/useMessages';
import {
  META_SEPARATOR,
  formatRiskGap,
  formatRiskNumber,
  formatRiskValue,
  hasMetaText,
  riskDisplayUnit,
} from '../logic/riskScorePresentation';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RiskStatusIndicator } from './RiskStatusIndicator';

interface RiskKpiSummaryProps {
  /** The root KPI of the metric group. */
  readonly metric: RiskMetric;
}

/** One part of the detail line: an optional bold label and the value after it. */
interface DetailPart {
  readonly key: string;
  readonly label?: string;
  readonly value: string | undefined;
}

/**
 * The detail line under the KPI value. Labels are bold so they read apart from the values; parts
 * with no value are left out, and the line is dropped when none remain.
 */
function KpiDetailLine({
  parts,
}: {
  readonly parts: readonly DetailPart[];
}): JSX.Element | null {
  const shown = parts.filter((part) => hasMetaText(part.value));
  if (shown.length === 0) return null;

  return (
    <Text fontSize="T100" color="color.text.secondary">
      {shown.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 ? META_SEPARATOR : null}
          {part.label ? <strong>{part.label}</strong> : null}
          {part.label ? ' ' : null}
          {part.value}
        </Fragment>
      ))}
    </Text>
  );
}

/**
 * The root KPI above the driver table: its name with the risk status beside it, the actual value
 * large with the unit beside it, and one detail line — target · % to target · the source's status
 * reason. Every figure is the API's value as sent; absent parts are left out of the line.
 */
export function RiskKpiSummary({ metric }: RiskKpiSummaryProps): JSX.Element {
  const { t } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);
  const { actualValue, targetValue, metricUnit, gapToTargetPct, statusReason } = metric;
  const unit = actualValue === undefined ? undefined : riskDisplayUnit(metricUnit);

  const details: readonly DetailPart[] = [
    {
      key: 'target',
      label: t('breakdownTargetLabel'),
      value:
        targetValue === undefined ? undefined : formatRiskValue(targetValue, metricUnit),
    },
    {
      key: 'gap',
      label: t('breakdownGapLabel'),
      value: gapToTargetPct === undefined ? undefined : formatRiskGap(gapToTargetPct),
    },
    { key: 'reason', value: statusReason },
  ];

  return (
    <Col gridGap="dimensions.spacing.50" minWidth={0}>
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        <Text fontWeight="bold">{metric.displayName ?? metric.metric}</Text>
        <RiskStatusIndicator status={metric.status} t={t} />
      </Row>
      <Row alignItems="baseline" gridGap="dimensions.spacing.100">
        <Text fontSize="T500" fontWeight="bold">
          {actualValue === undefined ? '—' : formatRiskNumber(actualValue)}
        </Text>
        {unit ? <Text color="color.text.secondary">{unit}</Text> : null}
      </Row>
      <KpiDetailLine parts={details} />
    </Col>
  );
}
